import React, {useRef, useCallback} from 'react';
import styled, {keyframes} from 'styled-components';
import {
   ControlButton,
   Glyphs,
   TscButtonType,
   TscStyles,
   TscThemeName,
   TscThemeNames
} from '@techsmith/tsc-cloud-style-guide';
import CssUtil from '../../util/CssUtil';
import CssConstants from '../../constants/cssConstants';
import {useTranslation} from 'react-i18next';
import {IEmojiReactionData} from '../../model/emojiReactionsModel';
import Constants from '../../constants/Constants';
import conversationApi from '../../service/conversationApi';
import config from '../../service/config';
import {getNativeEmojiById} from '../../util/EmojiSearch';
import {reactionsStore, updateReactionsState} from '../../context/reactionsProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {themeStore} from '../../context/themeProvider';

const showMenuAnimation = keyframes`
   0% {transform: scale(.25) translateX(inherit) translateY(inherit); opacity: 0;}
   100% {transform: scale(1) translateX(inherit) translateY(inherit); opacity: 1;}
`;

const ReactionsSummaryContainer = styled.div<{leftOffset: number; bottomOffset: number; maxHeight: number; theme: TscThemeNames}>`
   position: absolute;
   bottom: ${props => props.bottomOffset}px;
   left: ${props => props.leftOffset}px;
   display: block;
   animation: ${showMenuAnimation} 200ms  ease;
   overflow: auto;
   max-height: ${props => props.maxHeight}px;
   padding: 0.25rem 0!important;
   width: ${Constants.reactionsSummary.width}px;
   color: ${props => props.theme === TscThemeName.dawn ? TscStyles.color.text.mediumDark : TscStyles.color.text.light};
   backdrop-filter: blur(4px);
   background-color: ${props => props.theme === TscThemeName.dawn ? CssUtil.toRgba(TscStyles.color.ui.dawn.lightMedium, CssConstants.reactionsSummaryBackgroundOpacity) : CssUtil.toRgba(TscStyles.color.ui.dusk.mediumDark, CssConstants.reactionsSummaryBackgroundOpacity)};
   border-radius: ${TscStyles.border.radius.large};
   box-shadow: ${TscStyles.shadow.short};
   pointer-events: auto;
`;

const ReactionSummaryList = styled.ul`
   list-style-type: none!important;
   margin: 0!important;
   padding: 0!important;
`;

const ReactionSummaryItem = styled.li`
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin: 0 0.5rem!important;
   line-height: 2rem;
`;

const ReactionDetails = styled.div`
   display: flex;
`;

const ReactionDisplayName = styled.div`
   font-size: 0.875rem;
   font-weight: ${TscStyles.font.weight.semibold};
   margin: 0 .5rem!important;
`;

export const ReactionsSummaryPopupBase: React.FC<IReactionsSummaryPopupProps & IStateMappedProps> = ({reactionsToDisplay, leftOffset, bottomOffset, maxHeight, closeOnInteractions, onClose, reactions, theme}) => {
   const {t} = useTranslation();
   const summaryPopOver = useRef<HTMLDivElement>(null);
   const clickStartedOutsideOfMenu = useRef<boolean>(false);

   // will always trigger from window.addEventListener, so native...
   const onFocusOut = useCallback((e: FocusEvent): void => {
      if (e.relatedTarget && e.relatedTarget !== summaryPopOver.current && !summaryPopOver.current?.contains(e.relatedTarget as Node)) {
         onClose();
      }
   }, []);

   const resetClickStartedOutsideOfMenuValue = useCallback((): void => {
      clickStartedOutsideOfMenu.current = false;
   }, []);

   // will always trigger from window.addEventListener, so native MouseEvent...
   const onMouseDown = useCallback((e: MouseEvent): void => {
      if (summaryPopOver.current === e.target || summaryPopOver.current?.contains(e.target as Node)) {
         e.preventDefault();
      } else {
         clickStartedOutsideOfMenu.current = true;
         e.target?.addEventListener('scroll', resetClickStartedOutsideOfMenuValue);
         window.addEventListener('scroll', resetClickStartedOutsideOfMenuValue);
      }
   }, [resetClickStartedOutsideOfMenuValue]);

   // will always trigger from window.addEventListener, so native MouseEvent...
   const onMouseUp = useCallback((e: MouseEvent): void => {
      e.target?.removeEventListener('scroll', resetClickStartedOutsideOfMenuValue);
      window.removeEventListener('scroll', resetClickStartedOutsideOfMenuValue);

      if (clickStartedOutsideOfMenu.current) {
         e.preventDefault();
         onClose();
      }

      resetClickStartedOutsideOfMenuValue();
   }, [resetClickStartedOutsideOfMenuValue]);

   React.useEffect(() => {
      if (!closeOnInteractions) {
         return;
      }

      window.addEventListener('mousedown', onMouseDown);
      window.addEventListener('mouseup', onMouseUp);
      window.addEventListener('focusout', onFocusOut);

      // eslint-disable-next-line consistent-return
      return () => {
         window.removeEventListener('mousedown', onMouseDown);
         window.removeEventListener('mouseup', onMouseUp);
         window.removeEventListener('focusout', onFocusOut);
         window.removeEventListener('scroll', resetClickStartedOutsideOfMenuValue);
      };
   }, []);

   const deleteReaction = async (reaction: IEmojiReactionData) => {
      await conversationApi.deleteReaction(reaction.ReviewConversation.Id, reaction.ReactionExternalId);
      onReactionDelete(reaction);
   };
   const getReactionsSummary = (): React.ReactFragment => {
      const reactionSummary = reactionsToDisplay.map((reaction, index) => {
         const isReactionOwner = reaction.Owner.Id === config.user.TechSmithId;
         const reactionEmoji = `${getNativeEmojiById(reaction.EmojiId)}`;
         const reactionDisplayName = `${reaction.Owner.DisplayName || Constants.reactionMarkers.defaultTextForAnonymousReaction}`;
         return (<ReactionSummaryItem key={index}>
            <ReactionDetails>{reactionEmoji}<ReactionDisplayName>{reactionDisplayName}</ReactionDisplayName></ReactionDetails>
            {isReactionOwner && <ControlButton
               glyph={<Glyphs.Delete16x16 />}
               buttonType={TscButtonType.tertiary}
               title={t('conversations.deleteReactionTitle')}
               themeName={theme}
               onClick={() => {
                  void deleteReaction(reaction);
                  onClose();
               }}
            /> }
         </ReactionSummaryItem>);
      });
      return reactionSummary;
   };

   const onReactionDelete = (deletedReaction: IEmojiReactionData) => {
      const deletedReactionIndex = reactions.findIndex(reaction => reaction.ReactionExternalId === deletedReaction.ReactionExternalId);
      if (deletedReactionIndex > -1) {
         const updatedReactions = reactions.concat([]);
         updatedReactions.splice(deletedReactionIndex, 1);
         updateReactionsState({reactions: updatedReactions});
      }
   };

   return (
      <ReactionsSummaryContainer ref={summaryPopOver} leftOffset={leftOffset} bottomOffset={bottomOffset} maxHeight={maxHeight} theme={theme}>
         <ReactionSummaryList>
            {getReactionsSummary()}
         </ReactionSummaryList>
      </ReactionsSummaryContainer>
   );
};

export interface IReactionsSummaryPopupProps {
   reactionsToDisplay: IEmojiReactionData[];
   leftOffset: number;
   bottomOffset: number;
   maxHeight: number;
   closeOnInteractions: boolean;
   onClose: () => void;
}

export interface IStateMappedProps {
   reactions: IEmojiReactionData[];
   theme: TscThemeNames;
}

const mapStatesToProps = (
   {reactionsToDisplay, leftOffset, bottomOffset, maxHeight, closeOnInteractions, onClose}: IReactionsSummaryPopupProps,
   {reactions}: Partial<IStateMappedProps>,
   {theme}: Partial<IStateMappedProps>
): IReactionsSummaryPopupProps & IStateMappedProps => ({reactionsToDisplay, leftOffset, bottomOffset, maxHeight, closeOnInteractions, onClose, reactions, theme});

export default withMemoizedContexts(mapStatesToProps, reactionsStore, themeStore)(ReactionsSummaryPopupBase);
